import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import get from 'lodash/get';
import { useRouter } from 'next/router';

import { PRODUCT_FILTER_TYPE } from 'app/constants/product';

// Components.
import ProductCategoryList from 'app/components/product/ProductCategoryList';
import ReviewPageBanner from 'app/components/product/ReviewPageBanner';
import ProductListSort from 'app/components/product/ProductListSort';
import ProductDetailList from 'app/components/product/ProductDetailList';
import ProductFilter from 'app/components/product/ProductFilter';
import PrismicBanner from 'app/components/misc/PrismicBanner';
import SignUpBonusCard from 'app/components/review/SignUpBonusCard';
import AdSlot from 'app/components/misc/AdSlot';
import mixpanel, { trackedEvents } from 'app/utils/mixpanel';
import { getProducts } from 'app/dispatcher/productDispatcher';
import Button from 'app/seedly-component-library/button-new';
import Breadcrumbs from 'app/seedly-component-library/breadcrumbs';

// Constants.
import { PRISMIC_BANNER_TYPE } from 'app/constants/app';
import { getCurrentCategory } from 'app/selectors/productSelector';

import type { RootState } from 'app/reducer';
import BankProviderList from 'app/components/product/BankProviderList';
import analytics from 'app/utils/analytics';

const ReviewsContent = props => {
  const { type } = props;
  const router = useRouter();
  const dispatch = useDispatch();

  // Selectors
  const hasErrorFetching = useSelector(
    (state: RootState) => state.product.api.hasError,
  );
  const totalCount = useSelector(state =>
    get(state, 'product.meta.pagination.totalCount', 0),
  );
  const currentCategory = useSelector(getCurrentCategory);

  // States
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterId, setFilterId] = useState(null);
  const [isSignUpBonusChecked, setSignUpBonusChecked] = useState(false);

  let breadcrumbs = [{ title: 'All Products', href: '/reviews/' }];

  if (currentCategory) {
    const href =
      type === 'sign-up-bonus'
        ? `/reviews/sign-up-bonus/${currentCategory.slug}/`
        : `/reviews/${currentCategory.slug}/`;

    breadcrumbs = [
      ...breadcrumbs,
      {
        title: currentCategory.name,
        href,
      },
    ];
  }

  // Refs
  const productSection = useRef(null);
  const scrollToComponent = useRef(null);

  // Effects
  useEffect(() => {
    scrollToComponent.current = require('react-scroll-to-component');
  }, []);

  const handleFilterClick = id => {
    setIsFilterOpen(true);
    setFilterId(id);
  };

  const handleSignUpBonusChange = e => {
    setSignUpBonusChecked(e.target.checked);
  };

  return (
    <div className="flex flex-col gap-6">
      <ReviewPageBanner />
      <AdSlot type="product" slotKey="main" />
      <div className="flex flex-col gap-6 justify-center py-0 px-3 mx-auto my-0 w-full max-w-full text-neutral-700 lg:w-[1024px]">
        <Breadcrumbs items={breadcrumbs} />
        <div className="w-full flex">
          {type !== 'sign-up-bonus' && (
            <div className="invisible w-0 lg:visible lg:w-72 lg:mr-6">
              <div className="flex flex-col gap-4 w-full">
                <SignUpBonusCard
                  TitleComponent={
                    <>
                      Get amazing deals when you sign up for new financial
                      products on Seedly 🎁
                    </>
                  }
                  ctaText="See All Offers"
                  onCTAClick={() => {
                    mixpanel.track(trackedEvents.SignUpBonus, {
                      param: 'see_all_offers',
                    });
                    analytics.trackSeeMoreOfferButton();
                    router.push('/reviews/sign-up-bonus/');
                  }}
                />
                {currentCategory?.slug === 'credit-cards' && (
                  <BankProviderList />
                )}
                <ProductCategoryList />
                <AdSlot type="product" slotKey="medium-banner" variant="bare" />
              </div>
            </div>
          )}
          <div className="w-full lg:w-[712px]" ref={productSection}>
            {hasErrorFetching ? (
              <div className="h-fit flex flex-col items-center gap-4">
                <p> Something went wrong.</p>
                <Button
                  onClick={() =>
                    dispatch(
                      // @ts-ignore until getProducts is migrated to TS
                      getProducts({
                        categorySlug: currentCategory.slug,
                        sortType: PRODUCT_FILTER_TYPE.MOST_REVIEWED,
                      }),
                    )
                  }
                >
                  Refresh
                </Button>
              </div>
            ) : (
              <>
                <p className="text-neutral-500 mb-2">
                  {totalCount} Products Found
                </p>
                <ProductListSort
                  type={type}
                  isSignUpBonusChecked={isSignUpBonusChecked}
                  handleFilterClick={handleFilterClick}
                  handleSignUpBonusChange={handleSignUpBonusChange}
                />
                <ProductDetailList
                  isSignUpBonus={
                    type === 'sign-up-bonus' || isSignUpBonusChecked
                  }
                  hideSignUpBonusEntryPoint={type === 'sign-up-bonus'}
                  onPageChange={() => {
                    scrollToComponent.current(productSection.current, {
                      offset: -82,
                      align: 'top',
                      duration: 1000,
                    });
                  }}
                />
                <PrismicBanner type={PRISMIC_BANNER_TYPE.PRODUCT} />
              </>
            )}
          </div>
        </div>
        <ProductFilter
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          filterId={filterId}
          withCloseButton
        />
      </div>
    </div>
  );
};

export default ReviewsContent;
